import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Toaster as Sonner, toast } from "sonner"
import { Check, X, Info, AlertTriangle } from "lucide-react"

const readAppTheme = () => {
  if (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  ) {
    return "dark";
  }

  try {
    const shared = window.localStorage.getItem("pms-theme");
    if (shared === "dark" || shared === "light") return shared;

    const legacy = window.localStorage.getItem("pinmaker-login-theme");
    return legacy === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

const Toaster = ({ ...props }) => {
  const [theme, setTheme] = useState(readAppTheme);

  useEffect(() => {
    const syncTheme = () => {
      const nextTheme = readAppTheme();
      setTheme(current => current === nextTheme ? current : nextTheme);
    };

    const root = document.documentElement;
    const observer = new MutationObserver(syncTheme);

    observer.observe(root, {
      attributes: true,
      attributeFilter: ["class"],
    });

    window.addEventListener("storage", syncTheme);
    window.addEventListener("focus", syncTheme);
    syncTheme();

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("focus", syncTheme);
    };
  }, []);

  return createPortal(
  <Sonner
    theme={theme}
    className="toaster group"
    position="top-center"
    visibleToasts={1}
    style={{
      "--offset-top": "62px",
      "--mobile-offset-top": "62px",
      "--width": "360px",
    }}
    icons={{
      success: (
        <span className="pms-toast-icon pms-toast-icon--success" aria-label="Berhasil">
          <Check className="pms-status-symbol" size={17} strokeWidth={3} />
        </span>
      ),
      error: (
        <span className="pms-toast-icon pms-toast-icon--error" aria-label="Gagal">
          <X className="pms-status-symbol" size={17} strokeWidth={3} />
        </span>
      ),
      info: (
        <span className="pms-toast-icon pms-toast-icon--info" aria-label="Informasi">
          <Info className="pms-status-symbol" size={17} strokeWidth={2.5} />
        </span>
      ),
      warning: (
        <span className="pms-toast-icon pms-toast-icon--warning" aria-label="Peringatan">
          <AlertTriangle className="pms-status-symbol" size={17} strokeWidth={2.5} />
        </span>
      ),
      loading: (
        <span className="pin-maker-loading-mark" aria-label="Loading">
          <i /><i /><i /><i /><i />
        </span>
      ),
    }}
    duration={2000}
    toastOptions={{
      duration: 2000,
      classNames: {
        toast:
          "group toast !min-w-0 !px-5 !py-4 !text-sm rounded-2xl",
        description: "group-[.toast]:text-slate-500",
        actionButton:
          "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
        cancelButton:
          "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
      },
    }}
    {...props}
  />,
  document.body
  );
};


// Durasi notifikasi global PIN Maker Studio.
const durationAwareToast = new Proxy(toast, {
  get(target, property) {
    if (property === "loading") {
      return (message, options = {}) =>
        target.loading(message, {
          ...options,
          className: [options.className, "pms-loading-toast-center"].filter(Boolean).join(" "),
          duration: Infinity,
        });
    }

    if (property === "success") {
      return (message, options = {}) =>
        target.success(message, {
          ...options,
          className: [options.className, "pms-status-toast-position", "pms-toast-page-aligned"].filter(Boolean).join(" "),
          duration: 2000,
        });
    }

    if (
      property === "error" ||
      property === "warning" ||
      property === "info"
    ) {
      return (message, options = {}) =>
        target[property](message, {
          ...options,
          className: [options.className, "pms-status-toast-position", "pms-toast-page-aligned"].filter(Boolean).join(" "),
          duration: 2000,
        });
    }

    const value = Reflect.get(target, property, target);
    return typeof value === "function" ? value.bind(target) : value;
  },
});

export { Toaster, durationAwareToast as toast }
